import { type ComponentPropsWithoutRef, forwardRef, useState } from 'react';
import Link from 'next/link';
import { ClickTracking } from '@/components/ClickTracking';
import { ChevronDownIcon } from '@/icons';
import { Button, type ButtonProps } from '@/ui_v3';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/ui_v3/components/hover-card';
import { cn } from '@/utils/tailwind.util';
import { type GnbButtonType, type GnbSubButtonType } from '..';
type GnbButtonSize = NonNullable<ButtonProps['size']>;
export const GnbButton = ({
  button,
  size = 'sm'
}: {
  button: GnbButtonType;
  size?: GnbButtonSize;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  if (button.href) {
    const linkContent = <Link href={button.href} target={button.target} prefetch={false}>
        <GnbMainButton button={button} size={size} />
      </Link>;
    return button.wrapper ? <>{button.wrapper(linkContent)}</> : linkContent;
  }
  const hasSubButtons = 0 < (button.subButtons?.length ?? 0);
  if (!hasSubButtons) {
    return <GnbMainButton button={button} size={size} />;
  }
  return <HoverCard openDelay={50} closeDelay={50} open={isOpen} onOpenChange={open => {
    button.interactionMode !== 'click' && setIsOpen(open);
  }} data-sentry-element="HoverCard" data-sentry-component="GnbButton" data-sentry-source-file="GnbButton.tsx">
      <HoverCardTrigger onClick={() => {
      setIsOpen(prev => !prev);
    }} asChild data-sentry-element="HoverCardTrigger" data-sentry-source-file="GnbButton.tsx">
        <GnbMainButton button={button} isOpen={isOpen} size={size} data-sentry-element="GnbMainButton" data-sentry-source-file="GnbButton.tsx" />
      </HoverCardTrigger>
      <HoverCardContent align="start" className="flex flex-col rounded-xs border-assistive p-2xs" data-sentry-element="HoverCardContent" data-sentry-source-file="GnbButton.tsx">
        {button.subButtons?.map((subButton, index) => <GnbSubButton key={subButton.label + index} onClick={() => {
        subButton.onClick?.();
        setIsOpen(false);
      }} button={subButton} />)}
      </HoverCardContent>
    </HoverCard>;
};
const GnbMainButton = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<'button'> & {
  button: GnbButtonType;
  isOpen?: boolean;
  size?: GnbButtonSize;
}>(({
  button,
  isOpen,
  onClick,
  size = 'sm',
  ...rest
}, ref) => {
  const hasSubButtons = 0 < (button.subButtons?.length ?? 0);
  return <ClickTracking eventName={button.clickEventname} eventProperties={button.clickEventProperties}>
      <Button ref={ref} {...rest} variant="transparent" intent="neutral" size={size} onClick={onClick} leftSlot={button.icon} rightSlot={!hasSubButtons ? undefined : <ChevronDownIcon className={cn('transition-transform ease duration-50', isOpen && 'rotate-180')} size="xs" />}>
        {button.label}
      </Button>
    </ClickTracking>;
});
GnbMainButton.displayName = 'GnbMainButton';
const GnbSectionTitle = ({
  title
}: {
  title: string;
}) => <div className="flex h-[33px] items-center px-xs pb-3xs pt-sm" data-sentry-component="GnbSectionTitle" data-sentry-source-file="GnbButton.tsx">
    <span className="text-caption-lg-semibold text-alternative">{title}</span>
  </div>;
const GnbSubButton = ({
  button,
  onClick
}: ComponentPropsWithoutRef<'a'> & {
  button: GnbSubButtonType;
}) => {
  return <>
      {button.sectionTitle && <GnbSectionTitle title={button.sectionTitle} />}
      <ClickTracking eventName={button.clickEventname} eventProperties={button.clickEventProperties} data-sentry-element="ClickTracking" data-sentry-source-file="GnbButton.tsx">
        <Link key={button.label} href={button.href} target={button.target} prefetch={false} className={cn('flex items-center', button.className)} onClick={onClick} data-sentry-element="Link" data-sentry-source-file="GnbButton.tsx">
          <button className={cn('flex flex-col items-start w-full min-w-[188px] rounded-xs p-xs text-body-md-medium hover:bg-gray-50')}>
            {button.subLabel && <span className={'text-caption-md-semibold text-assistive'}>
                {button.subLabel}
              </span>}
            <div className={'flex items-center gap-xs'}>
              <div className="p-[2px]">{button.icon}</div>
              {button.label}
            </div>
          </button>
        </Link>
      </ClickTracking>
    </>;
};